import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Tent, Euro, Search, CheckCircle2, AlertCircle, XCircle, Layers, Settings2, AlertTriangle, BedDouble, CalendarRange, HeartPulse } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { RUOLI_LABEL } from "@/context/AuthContext";
import { BottomSheet, Empty, DeleteButton, Field, BulkCheck, BulkBar, ConfirmAction, blurSuTouch } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { CamposcuolaBilancioProvider, useCamposcuolaBilancio } from "@/context/CamposcuolaBilancioContext";
import { vociSpese, vociEntrate } from "@/lib/camposcuolaEngine";

const STATO_BADGE = {
  pagato: <span className="badge-pos">Pagato</span>,
  parziale: <span className="badge-warn">Parziale</span>,
  da_pagare: <span className="badge-neg">Da pagare</span>,
  escluso: <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">Escluso</span>,
};

const RUOLI_CAMPO_LABEL = { animatore: "Animatore", responsabile: "Responsabile", ospite: "Ospite" };

// Motivo della riduzione: menu CHIUSO. Era testo libero, e la motivazione
// tipica è una condizione economica, familiare o di salute della famiglia.
const MOTIVI_OVERRIDE = [
  ["riduzione_deliberata", "Riduzione deliberata"],
  ["quota_parziale", "Quota parziale"],
  ["piu_fratelli", "Più fratelli"],
  ["permanenza_ridotta", "Permanenza ridotta"],
  ["altro", "Altro"],
];
const ETICHETTA_MOTIVO = Object.fromEntries(MOTIVI_OVERRIDE);
const RUOLI_CAMPO_PLURALE = { animatore: "Animatori", responsabile: "Responsabili", ospite: "Ospiti" };

// Chi ha un account eredita il ruolo camposcuola dal ruolo dell'account
const RUOLO_CAMPO_DA_ACCOUNT = { admin: "responsabile", responsabile: "responsabile", animatore: "animatore", aiuto_animatore: "animatore" };

// Pills per il filtro di stato pagamento
const FILTRI = [
  { key: "tutti",     label: "Tutti",      icon: Layers },
  { key: "pagato",    label: "Pagato",     icon: CheckCircle2 },
  { key: "parziale",  label: "Parziale",   icon: AlertCircle },
  { key: "da_pagare", label: "Da pagare",  icon: XCircle },
];

// Formato italiano col simbolo davanti, coerente col resto dell'app: € 9.961,00
const FMT_NUM = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eur = (n) => `€ ${FMT_NUM.format(n ?? 0)}`;
const nottiLabel = (n) => `${n} nott${n === 1 ? "e" : "i"}`;

// Stato calcolato dal dovuto del MOTORE (non dal campo storico del db)
const statoDi = (riga) => {
  if (riga.tipo_quota === "escluso") return "escluso";
  const versato = riga.partecipante.importo_versato || 0;
  if (versato >= riga.dovuto) return "pagato";
  if (versato <= 0) return "da_pagare";
  return "parziale";
};

// Palette per sezione: i costi virano al rosato, le entrate al verde.
// Tinte tenui: il colore conferma la lettura, il contenitore fa il lavoro di separazione.
const TEMA = {
  costi: {
    pannello: "bg-rose-50/50 border-rose-100",
    titolo: "text-rose-900/70",
    totale: "text-rose-700",
    tessera: "bg-white/80 border-rose-100 hover:border-rose-300",
    azione: "text-rose-400 hover:text-rose-600",
  },
  entrate: {
    pannello: "bg-emerald-50/50 border-emerald-100",
    titolo: "text-emerald-900/70",
    totale: "text-emerald-700",
    tessera: "bg-white/80 border-emerald-100 hover:border-emerald-300",
    azione: "text-emerald-500 hover:text-emerald-700",
  },
};

// Tessera cliccabile dentro un pannello: piatta, è il pannello a portare l'ombra
function CardImporto({ titolo, importo, sub, onClick, tema, testId }) {
  const t = TEMA[tema];
  return (
    <button type="button" onClick={onClick}
      className={`rounded-2xl border px-3.5 py-3 text-left w-full transition-all active:scale-[0.98] ${t.tessera}`}
      data-testid={testId}>
      {/* Titolo su 2 righe invece che troncato: su mobile le tessere sono strette
          e "Quote partecipanti · struttura" tagliato non si capisce. */}
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide leading-tight line-clamp-2 min-h-[1.75em]">{titolo}</p>
      <p className="font-display text-lg font-extrabold leading-tight text-slate-900 mt-0.5">{eur(importo)}</p>
      {sub && <p className="text-[11px] text-slate-400 font-semibold truncate mt-0.5">{sub}</p>}
    </button>
  );
}

// Pannello di sezione: intestazione con totale, azione "+ voce", tessere all'interno
function SezionePannello({ tema, titolo, totale, onAdd, addLabel, testId, children }) {
  const t = TEMA[tema];
  return (
    <section className={`rounded-3xl border p-4 ${t.pannello}`}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <p className={`text-xs font-bold uppercase tracking-[0.1em] ${t.titolo}`}>{titolo}</p>
          <p className={`font-display text-base font-extrabold shrink-0 ${t.totale}`} data-testid={testId}>{eur(totale)}</p>
        </div>
        <button type="button" onClick={onAdd}
          className={`text-[11px] font-bold transition-colors shrink-0 inline-flex items-center gap-1 ${t.azione}`}
          data-testid={`${testId}-add`}>
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2.5">{children}</div>
    </section>
  );
}

export default function Camposcuola() {
  const [dati, setDati] = useState(null);
  // Filtro per annata: primo pezzo del lotto 12, qui è sicuro perché il
  // camposcuola non aggrega fra stagioni diverse.
  const [stagione, setStagione] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [utenti, setUtenti] = useState([]);

  const load = useCallback(() => {
    api.get(`/camposcuola${stagione ? `?stagione=${encodeURIComponent(stagione)}` : ""}`).then((r) => setDati(r.data));
    api.get("/utenti").then((r) => setUtenti(r.data));
  }, [stagione]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  if (!dati) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  return (
    <CamposcuolaBilancioProvider>
      <CamposcuolaInner dati={dati} utenti={utenti} load={load}
        stagione={stagione} setStagione={setStagione}
        bulkMode={bulkMode} setBulkMode={setBulkMode}
        selected={selected} setSelected={setSelected} />
    </CamposcuolaBilancioProvider>
  );
}

function CamposcuolaInner({ dati, utenti, load, stagione, setStagione,
                            bulkMode, setBulkMode, selected, setSelected }) {
  const { impostazioni, bilancio, actions } = useCamposcuolaBilancio();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [impOpen, setImpOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);
  const [impForm, setImpForm] = useState(null);
  const [dettaglio, setDettaglio] = useState(null);   // sheet "come si arriva a questo numero"
  const [voceEdit, setVoceEdit] = useState(null);     // sheet voce libera {tipo, id, descrizione, importo}
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filtroStato, setFiltroStato] = useState("tutti");

  // Il server resta la fonte di verità: ad ogni fetch reidrata lo store.
  useEffect(() => {
    actions.hydrate(dati.impostazioni, dati.partecipanti);
  }, [dati, actions]);

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/camposcuola/bulk/delete", { ids: selected });
      toast.success(`${data.deleted} partecipanti rimossi`);
      setBulkMode(false); setSelected([]); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const righeById = useMemo(() => {
    const m = new Map();
    bilancio.righe.forEach((r) => m.set(r.partecipante.id, r));
    return m;
  }, [bilancio]);

  const costoNotte = Number(impostazioni.costo_notte_struttura) || 0;
  const nottiTot = Number(impostazioni.notti_totali_evento) || 1;
  const listaVociSpese = vociSpese(impostazioni);
  const listaVociEntrate = vociEntrate(impostazioni);

  // ---------- Dati derivati per le card ----------

  const attive = useMemo(() => bilancio.righe.filter((r) => r.tipo_quota !== "escluso"), [bilancio]);

  // Ordine di presentazione richiesto: Responsabili, Animatori, Ospiti
  const gruppiRuolo = useMemo(
    () => ["responsabile", "animatore", "ospite"]
      .map((ruolo) => ({ ruolo, righe: attive.filter((r) => r.partecipante.ruolo === ruolo) }))
      .filter((g) => g.righe.length > 0),
    [attive]
  );

  // Raggruppa righe per numero di notti → [{notti, count, importoUnit}]
  const perNotti = (righe, importoUnit) => {
    const m = new Map();
    righe.forEach((r) => m.set(r.notti, (m.get(r.notti) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[0] - a[0]).map(([notti, count]) => ({ notti, count, unit: importoUnit(notti) }));
  };

  const margineQuota = bilancio.quota_applicata !== null ? bilancio.quota_applicata - nottiTot * costoNotte : 0;

  // Card unica "Struttura": una riga per ruolo (Responsabili, Animatori, Ospiti);
  // se dentro un ruolo ci sono notti diverse, quel ruolo si spezza in più righe.
  const totaleStruttura = useMemo(() => attive.reduce((a, r) => a + r.costo_struttura, 0), [attive]);

  const apriDettaglioStruttura = () => {
    const righe = [];
    gruppiRuolo.forEach((g) => {
      perNotti(g.righe, (notti) => notti * costoNotte).forEach(({ notti, count, unit }) => {
        const etichetta = count === 1 ? RUOLI_CAMPO_LABEL[g.ruolo] : RUOLI_CAMPO_PLURALE[g.ruolo];
        righe.push({
          label: `${count} ${etichetta} × ${nottiLabel(notti)} × ${eur(costoNotte)}`,
          value: eur(count * unit),
        });
      });
    });
    setDettaglio({
      titolo: "Struttura",
      righe,
      totale: totaleStruttura,
      note: "Costo richiesto dalla struttura, in base alle notti effettive di ciascun partecipante. Chi ha 0 notti è escluso.",
    });
  };

  // Card unica "Quote partecipanti · parte struttura": riga per ruolo (e per notti se differiscono)
  const totaleQuoteStruttura = useMemo(
    () => bilancio.righe.reduce((a, r) => a + r.quota_struttura, 0),
    [bilancio]
  );

  const apriDettaglioQuote = () => {
    const righe = [];
    gruppiRuolo.forEach((g) => {
      const qb = g.righe.filter((r) => r.tipo_quota === "quota_base");
      if (qb.length === 0) return;
      perNotti(qb, (notti) => notti * costoNotte).forEach(({ notti, count, unit }) => {
        const etichetta = count === 1 ? RUOLI_CAMPO_LABEL[g.ruolo] : RUOLI_CAMPO_PLURALE[g.ruolo];
        righe.push({
          label: `${count} ${etichetta} × ${nottiLabel(notti)} × ${eur(costoNotte)}`,
          value: eur(count * unit),
        });
      });
    });
    setDettaglio({
      titolo: "Quote partecipanti · parte struttura",
      righe,
      totale: totaleQuoteStruttura,
      note: `Ognuno versa in tutto ${bilancio.quota_applicata !== null ? eur(bilancio.quota_applicata) : "—"} (meno ${eur(costoNotte)} per ogni notte saltata): qui c'è la parte che copre la propria struttura, la maggiorazione di ${eur(margineQuota)} a testa è nella card dedicata.`,
    });
  };

  const apriDettaglioMaggiorazione = () => {
    setDettaglio({
      titolo: "Maggiorazione quota",
      righe: [
        { label: `Quota ${bilancio.quota_applicata !== null ? eur(bilancio.quota_applicata) : "—"} − struttura piena ${eur(nottiTot * costoNotte)} (${nottiLabel(nottiTot)} × ${eur(costoNotte)})`, value: `${eur(margineQuota)} a testa` },
        { label: `${bilancio.n_quota_standard} partecipanti a quota base × ${eur(margineQuota)}`, value: eur(bilancio.maggiorazione_quota) },
      ],
      totale: bilancio.maggiorazione_quota,
      note: "È il margine sopra il costo struttura chiesto a chi paga la quota base. Resta identico anche per chi fa meno notti (paga solo le notti in meno).",
    });
  };

  // Quote fisse raggruppate per ruolo + importo + motivo (invece di elencare ogni nome)
  const apriDettaglioQuoteFisse = () => {
    const righe = [];
    gruppiRuolo.forEach((g) => {
      const ov = g.righe.filter((r) => r.tipo_quota === "override");
      if (ov.length === 0) return;
      const m = new Map();
      ov.forEach((r) => {
        const motivo = r.partecipante.motivo_override || "";
        const chiave = `${r.dovuto}|${motivo}`;
        const prev = m.get(chiave) || { importo: r.dovuto, motivo, count: 0 };
        m.set(chiave, { ...prev, count: prev.count + 1 });
      });
      [...m.values()]
        .sort((a, b) => b.count - a.count || b.importo - a.importo)
        .forEach(({ importo, motivo, count }) => {
          const etichetta = count === 1 ? RUOLI_CAMPO_LABEL[g.ruolo] : RUOLI_CAMPO_PLURALE[g.ruolo];
          righe.push({
            label: `${count} ${etichetta} × ${eur(importo)}${motivo ? ` (${motivo})` : ""}`,
            value: eur(count * importo),
          });
        });
    });
    setDettaglio({
      titolo: "Quote fisse (override)",
      righe,
      totale: bilancio.totale_override,
      note: "Quote bloccate manualmente (staff, sconti…): sono fuori dal calcolo della quota base. Gli sconti rispetto alla quota piena restano a carico della parrocchia (GAP).",
    });
  };

  const apriDettaglioGap = () => {
    setDettaglio({
      titolo: "Quota parrocchia (GAP)",
      righe: [
        { label: "Totale costi (struttura + voci di costo)", value: eur(bilancio.spese_totali) },
        { label: "− Quote a quota base (struttura + maggiorazione)", value: `− ${eur(bilancio.righe.reduce((a, r) => a + (r.tipo_quota === "quota_base" ? r.dovuto : 0), 0))}` },
        { label: "− Quote fisse (override)", value: `− ${eur(bilancio.totale_override)}` },
        { label: "− Voci d'entrata", value: `− ${eur(bilancio.totale_voci_entrate)}` },
      ],
      totale: bilancio.gap,
      note: "È il massimo a carico della parrocchia: quello che resta scoperto se tutti versano il dovuto. Ogni avanzo torna alla parrocchia.",
    });
  };

  // ---------- Persistenza impostazioni / voci ----------

  const persistImpostazioni = (patch = {}) =>
    api.put("/camposcuola/impostazioni", {
      costo_notte_struttura: Number(impostazioni.costo_notte_struttura) || 0,
      notti_totali_evento: Math.max(1, parseInt(impostazioni.notti_totali_evento, 10) || 1),
      override_globale_quota_base: impostazioni.override_globale_quota_base ?? null,
      voci_spese_extra: listaVociSpese,
      voci_entrate: listaVociEntrate,
      ...patch,
    });

  const apriVoce = (tipo, voce = null) =>
    setVoceEdit(voce
      ? { tipo, id: voce.id, descrizione: voce.descrizione, importo: String(voce.importo) }
      : { tipo, id: null, descrizione: "", importo: "" });

  const salvaVoce = async (e) => {
    e.preventDefault();
    const lista = voceEdit.tipo === "spesa" ? [...listaVociSpese] : [...listaVociEntrate];
    const voce = {
      id: voceEdit.id || (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now())),
      descrizione: voceEdit.descrizione.trim(),
      importo: Number(voceEdit.importo) || 0,
    };
    const idx = lista.findIndex((v) => v.id === voce.id);
    if (idx >= 0) lista[idx] = voce; else lista.push(voce);
    try {
      await persistImpostazioni(voceEdit.tipo === "spesa" ? { voci_spese_extra: lista } : { voci_entrate: lista });
      toast.success("Voce salvata");
      setVoceEdit(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const eliminaVoce = async () => {
    const lista = (voceEdit.tipo === "spesa" ? listaVociSpese : listaVociEntrate).filter((v) => v.id !== voceEdit.id);
    try {
      await persistImpostazioni(voceEdit.tipo === "spesa" ? { voci_spese_extra: lista } : { voci_entrate: lista });
      toast.success("Voce eliminata");
      setVoceEdit(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ---------- Partecipante ----------

  const utentiGiaInseriti = useMemo(() => {
    const set = new Set();
    (dati?.partecipanti || []).forEach((p) => { if (p.tipo === "utente" && p.utente_id) set.add(p.utente_id); });
    return set;
  }, [dati]);
  const utentiSelezionabili = useMemo(
    () => utenti.filter((u) => !utentiGiaInseriti.has(u.id) || (editId && form?.utente_id === u.id)),
    [utenti, utentiGiaInseriti, editId, form]
  );

  // ---- Aggiunta in blocco dello staff (animatori, aiuti, responsabili) ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSel, setBulkSel] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const staffAggiungibile = useMemo(
    () => utenti.filter((u) => !utentiGiaInseriti.has(u.id)),
    [utenti, utentiGiaInseriti]
  );
  const bulkFiltrati = useMemo(() => {
    const q = bulkSearch.trim().toLowerCase();
    return q ? staffAggiungibile.filter((u) => `${u.nome} ${u.cognome} ${u.username}`.toLowerCase().includes(q)) : staffAggiungibile;
  }, [staffAggiungibile, bulkSearch]);

  const salvaBulk = async () => {
    if (bulkSel.length === 0) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/camposcuola/bulk-staff", { utente_ids: bulkSel });
      toast.success(`${data.aggiunti} aggiunt${data.aggiunti === 1 ? "o" : "i"} al camposcuola`);
      setBulkOpen(false); setBulkSel([]); setBulkSearch("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBulkSaving(false); }
  };

  // Sheet unico di aggiunta: tab "staff" (multi-selezione) o "ospite" (esterno)
  const [addTab, setAddTab] = useState("staff");

  const apriAggiungi = () => {
    setEditId(null);
    setForm({ tipo: "esterno", utente_id: "", nome: "", cognome: "", contatto: "", sesso: "",
      ruolo: "ospite", notti_effettive: "", override_quota: "", motivo_override: "", importo_versato: "0",
      info_sanitarie: "", consenso_dati_sanitari: false });
    setBulkSel([]);
    setBulkSearch("");
    setAddTab("staff");
    setBulkOpen(true);
  };

  const apri = (p = null) => {
    setEditId(p?.id || null);
    setForm(p
      ? { tipo: p.tipo, utente_id: p.utente_id || "",
          nome: p.tipo === "esterno" ? p.nome || "" : "",
          cognome: p.tipo === "esterno" ? (p.cognome_base ?? p.cognome ?? "") : "",
          contatto: p.contatto || "",
          sesso: p.tipo === "esterno" ? (p.sesso || "") : "",
          ruolo: p.ruolo || "animatore",
          notti_effettive: p.notti_effettive ?? "",
          override_quota: p.override_quota ?? "",
          motivo_override: p.motivo_override || "",
          info_sanitarie: p.info_sanitarie || "",
          consenso_dati_sanitari: !!p.consenso_dati_sanitari,
          importo_versato: p.importo_versato }
      : { tipo: "utente", utente_id: "", nome: "", cognome: "", contatto: "", sesso: "",
          ruolo: "animatore", notti_effettive: "", override_quota: "", motivo_override: "", importo_versato: "0",
      info_sanitarie: "", consenso_dati_sanitari: false });
    setSheetOpen(true);
  };

  const ruoloEffettivo = useMemo(() => {
    if (!form) return "animatore";
    if (form.tipo === "utente") {
      const u = utenti.find((x) => x.id === form.utente_id);
      return u ? RUOLO_CAMPO_DA_ACCOUNT[u.role] || "animatore" : form.ruolo;
    }
    return form.ruolo;
  }, [form, utenti]);

  // Anteprima del dovuto secondo il motore (con la regola delle notti GAP-neutrale)
  const dovutoPreview = useMemo(() => {
    if (!form) return 0;
    const notti = form.notti_effettive === "" ? nottiTot : Number(form.notti_effettive);
    if (notti === 0) return 0;
    if (form.override_quota !== "") return Number(form.override_quota) || 0;
    if ((ruoloEffettivo === "animatore" || ruoloEffettivo === "ospite") && bilancio.quota_applicata !== null) {
      return Math.max(0, bilancio.quota_applicata - (nottiTot - Math.min(notti, nottiTot)) * costoNotte);
    }
    return 0;
  }, [form, ruoloEffettivo, bilancio.quota_applicata, nottiTot, costoNotte]);

  const salva = async (e) => {
    e.preventDefault();
    setSaving(true);
    const overrideVal = form.override_quota === "" ? null : Number(form.override_quota);
    const payload = {
      tipo: form.tipo,
      utente_id: form.tipo === "utente" ? form.utente_id : null,
      nome: form.tipo === "esterno" ? form.nome : null,
      cognome: form.tipo === "esterno" ? form.cognome : null,
      codice_fiscale: null,
      contatto: form.tipo === "esterno" ? form.contatto || null : null,
      sesso: form.tipo === "esterno" ? (form.sesso || null) : null,
      ruolo: ruoloEffettivo,
      notti_effettive: form.notti_effettive === "" ? null : Number(form.notti_effettive),
      override_quota: overrideVal,
      motivo_override: overrideVal !== null ? form.motivo_override || null : null,
      info_sanitarie: (form.info_sanitarie || "").trim() || null,
      consenso_dati_sanitari: form.consenso_dati_sanitari ? true : null,
      importo_dovuto: dovutoPreview, // snapshot per compatibilità: la verità è il motore
      importo_versato: Number(form.importo_versato || 0),
    };
    try {
      const { data: saved } = editId
        ? await api.put(`/camposcuola/${editId}`, payload)
        : await api.post("/camposcuola", payload);
      if (!editId && saved?.tipo === "esterno" && (saved.disambigua || 1) > 1) {
        toast.warning(`Esiste già «${saved.nome} ${saved.cognome_base}»: salvato come «${saved.nome} ${saved.cognome}» per distinguerlo`);
      } else {
        toast.success("Partecipante salvato");
      }
      setSheetOpen(false);
      setBulkOpen(false); // il form ospite vive anche nello sheet "Aggiungi"
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/camposcuola/${editId}`);
      toast.success("Partecipante rimosso");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ---------- Impostazioni evento (what-if live sullo store) ----------

  const apriImpostazioni = () => {
    setImpForm({
      costo_notte_struttura: String(impostazioni.costo_notte_struttura ?? 0),
      notti_totali_evento: String(impostazioni.notti_totali_evento ?? 1),
      override_globale_quota_base: impostazioni.override_globale_quota_base ?? "",
    });
    setImpOpen(true);
  };

  const patchImp = (campo, valore) => {
    setImpForm((f) => ({ ...f, [campo]: valore }));
    const parsed =
      campo === "override_globale_quota_base"
        ? (valore === "" ? null : Number(valore) || 0)
        : campo === "notti_totali_evento"
          ? Math.max(1, parseInt(valore, 10) || 1)
          : Number(valore) || 0;
    actions.setImpostazioni({ [campo]: parsed });
  };

  const chiudiImpostazioni = (open) => {
    setImpOpen(open);
    if (!open) load(); // scarta il what-if non salvato
  };

  const salvaImpostazioni = async (e) => {
    e.preventDefault();
    try {
      await persistImpostazioni({
        costo_notte_struttura: Number(impForm.costo_notte_struttura) || 0,
        notti_totali_evento: Math.max(1, parseInt(impForm.notti_totali_evento, 10) || 1),
        override_globale_quota_base: impForm.override_globale_quota_base === "" ? null : Number(impForm.override_globale_quota_base) || 0,
      });
      toast.success("Impostazioni evento salvate");
      setImpOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Form partecipante unico: usato sia per la MODIFICA (sheet dedicato) sia
  // per l'aggiunta di un OSPITE esterno (tab dentro lo sheet "Aggiungi").
  // Gli utenti con account si aggiungono solo dalla multi-selezione staff.
  const formPartecipante = form && (
    <form onSubmit={salva}>
      {form.tipo === "utente" ? (
        <Field label="Utente" hint="Il ruolo al campo è ereditato dall'account">
          <select className="input-app" required disabled={!!editId} value={form.utente_id}
            onChange={(e) => { setForm({ ...form, utente_id: e.target.value }); blurSuTouch(e); }}
            data-testid="partecipante-utente-select">
            <option value="">Seleziona...</option>
            {utentiSelezionabili.map((u) => <option key={u.id} value={u.id}>{u.nome} {u.cognome} ({RUOLI_LABEL[u.role]})</option>)}
          </select>
        </Field>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome"><input className="input-app" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="partecipante-nome-input" /></Field>
            <Field label="Cognome"><input className="input-app" required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="partecipante-cognome-input" /></Field>
          </div>
          <Field label="Contatto (facoltativo)"><input className="input-app" value={form.contatto} onChange={(e) => setForm({ ...form, contatto: e.target.value })} data-testid="partecipante-contatto-input" /></Field>
          <Field label="Sesso" hint="Serve alla generazione delle stanze">
            <select className="input-app" value={form.sesso || ""}
              onChange={(e) => { setForm({ ...form, sesso: e.target.value }); blurSuTouch(e); }}
              data-testid="partecipante-sesso-select">
              <option value="">Seleziona...</option>
              <option value="M">Maschile (M)</option>
              <option value="F">Femminile (F)</option>
            </select>
          </Field>
        </>
      )}

      <div className="grid grid-cols-2 gap-3">
        {form.tipo === "utente" ? (
          <Field label="Ruolo al campo" hint="Ereditato dal ruolo dell'account">
            <input className="input-app bg-slate-50 text-slate-500" disabled value={form.utente_id ? RUOLI_CAMPO_LABEL[ruoloEffettivo] : "—"} data-testid="partecipante-ruolo-derivato" />
          </Field>
        ) : (
          <Field label="Ruolo al campo">
            <select className="input-app" value={form.ruolo}
              onChange={(e) => { setForm({ ...form, ruolo: e.target.value }); blurSuTouch(e); }}
              data-testid="partecipante-ruolo-select">
              {Object.entries(RUOLI_CAMPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        )}
        <Field label="Notti" hint={form.notti_effettive === "" ? `Eredita dall'evento (${nottiTot})` : Number(form.notti_effettive) === 0 ? "0 = escluso da costi e quote" : `Paga ${eur(costoNotte)} in meno per ogni notte saltata`}>
          <input type="number" min="0" max={nottiTot} className="input-app" placeholder={String(nottiTot)}
            value={form.notti_effettive} onChange={(e) => setForm({ ...form, notti_effettive: e.target.value })} data-testid="partecipante-notti-input" />
        </Field>
      </div>

      <Field label="Quota personalizzata (€, facoltativa)" hint={form.override_quota === "" ? (ruoloEffettivo !== "responsabile" ? "Vuota: segue la quota base calcolata" : "Vuota: nessuna quota (verrà segnalato in bilancio)") : "Quota bloccata: la differenza dalla quota piena resta a carico della parrocchia"}>
        <div className="relative">
          <Euro className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="number" step="0.01" min="0" className="input-app !pl-10" value={form.override_quota}
            onChange={(e) => setForm({ ...form, override_quota: e.target.value })} data-testid="partecipante-override-input" placeholder={ruoloEffettivo !== "responsabile" && bilancio.quota_applicata !== null ? bilancio.quota_applicata.toFixed(2) : "es. 35"} />
        </div>
      </Field>
      {form.override_quota !== "" && (
        <Field label="Motivo (facoltativo)" hint="Scelta chiusa: la ragione di una riduzione è spesso una condizione economica o familiare, e non va scritta per esteso.">
          <select className="input-app" value={form.motivo_override}
            onChange={(e) => { setForm({ ...form, motivo_override: e.target.value }); blurSuTouch(e); }}
            data-testid="partecipante-motivo-input">
            <option value="">Non indicato</option>
            {MOTIVI_OVERRIDE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      )}

      {/* --- Informazioni sanitarie (art. 9): qui, non nel profilo generale.
           La finalità è la gestione dei pasti di QUESTA edizione, quindi il
           dato vive con l'edizione e scade 30 giorni dopo il campo. --- */}
      <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 mb-4" data-testid="camposcuola-sanitario">
        <p className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-1">
          <HeartPulse className="h-4 w-4 text-rose-500" /> Informazioni sanitarie
        </p>
        <p className="text-xs text-slate-600 font-semibold mb-2">
          Allergie, intolleranze o esigenze alimentari rilevanti per i pasti.
        </p>
        <textarea className="input-app" rows={2} maxLength={1000}
          value={form.info_sanitarie || ""}
          onChange={(e) => setForm({ ...form, info_sanitarie: e.target.value })}
          data-testid="partecipante-sanitarie-input" />
        <p className="text-xs text-rose-800 bg-rose-100 border border-rose-200 rounded-xl p-3 font-semibold mt-2">
          Visibile solo ai Responsabili incaricati dei pasti. Conservata in forma cifrata
          e cancellata automaticamente 30 giorni dopo la fine del campo.
        </p>
        {(form.info_sanitarie || "").trim() && (
          <>
            <label className="flex items-start gap-3 cursor-pointer mt-3">
              <input type="checkbox" className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
                checked={!!form.consenso_dati_sanitari}
                onChange={(e) => setForm({ ...form, consenso_dati_sanitari: e.target.checked })}
                data-testid="partecipante-consenso-sanitario" />
              <span className="text-xs text-slate-700 font-medium leading-relaxed">
                L'interessato (o chi esercita la responsabilità genitoriale, se minorenne)
                acconsente al trattamento di queste informazioni per la sola gestione dei pasti.
              </span>
            </label>
            {!form.consenso_dati_sanitari && (
              <p className="flex items-start gap-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
                Senza consenso l'iscrizione va comunque a buon fine, ma queste informazioni
                non verranno conservate e in cucina non se ne potrà tenere conto.
              </p>
            )}
          </>
        )}
      </div>

      <Field label="Importo versato (€)">
        <div className="relative">
          <Euro className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="number" step="0.01" min="0" className="input-app !pl-10" required value={form.importo_versato} onChange={(e) => setForm({ ...form, importo_versato: e.target.value })} data-testid="partecipante-versato-input" />
        </div>
      </Field>

      {/* Dovuto + Salva sempre a portata: il form è lungo e il dovuto cambia
          mentre si modificano notti e quota. */}
      <div className="sticky bottom-0 -mx-5 px-5 pt-2 pb-1 bg-gradient-to-t from-white via-white to-white/0">
        <div className="rounded-2xl bg-slate-50 p-3 mb-3 flex items-center justify-between" data-testid="partecipante-dovuto-preview">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Dovuto risultante</p>
          <p className="font-display font-extrabold text-slate-900">{eur(dovutoPreview)}</p>
        </div>
        <button className="btn-primary w-full" disabled={saving} data-testid="partecipante-submit-btn">{saving ? "Salvataggio..." : "Salva"}</button>
      </div>
      {editId && (
        <div className="mt-3 flex justify-end">
          <DeleteButton onConfirm={elimina} entity="questo partecipante" testId="partecipante-delete-btn" />
        </div>
      )}
    </form>
  );

  return (
    <div data-testid="camposcuola-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Camposcuola</h1>
          <p className="text-sm text-slate-500 font-semibold">Partecipanti, pagamenti e bilancio</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/camposcuola/stanze" className="btn-ghost" data-testid="generatore-stanze-btn">
            <BedDouble className="h-4 w-4" /> Generatore Stanze
          </Link>
          <button className="btn-ghost" onClick={apriImpostazioni} data-testid="impostazioni-evento-btn">
            <Settings2 className="h-4 w-4" /> Evento
          </button>
          <button className="btn-primary" onClick={apriAggiungi} data-testid="aggiungi-btn">
            <Plus className="h-4 w-4" /> Aggiungi
          </button>
        </div>
      </div>

      {/* PARAMETRI DEL CALCOLO + eventuali anomalie */}
      <div className="card-app p-4 mb-4" data-testid="bilancio-panel">
        <p className="text-xs text-slate-500 font-semibold" data-testid="bilancio-quota">
          Quota base: <span className="font-extrabold text-slate-800">{bilancio.quota_applicata !== null ? eur(bilancio.quota_applicata) : "—"}</span>
          {bilancio.fonte_quota === "override_globale" && bilancio.quota_matematica !== null && (
            <span className="text-slate-400"> (matematica: {eur(bilancio.quota_matematica)})</span>
          )}
          {" "}· {bilancio.n_quota_standard} partecipant{bilancio.n_quota_standard === 1 ? "e" : "i"} a quota standard
          {" "}· {nottiLabel(nottiTot)} × {eur(costoNotte)}
        </p>
        {bilancio.anomalie.length > 0 && (
          <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="bilancio-anomalie">
            {bilancio.anomalie.map((a, i) => (
              <p key={i} className="text-xs font-semibold text-amber-700 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {a}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* COSTI ED ENTRATE: due pannelli contenuti e tinti */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <SezionePannello tema="costi" titolo="Costi da sostenere" totale={bilancio.spese_totali}
          onAdd={() => apriVoce("spesa")} addLabel="Voce di costo" testId="totale-costi">
          {attive.length > 0 && (
            <CardImporto tema="costi" testId="card-struttura"
              titolo="Struttura"
              importo={totaleStruttura}
              sub={`${attive.length} person${attive.length === 1 ? "a" : "e"} · dettaglio`}
              onClick={apriDettaglioStruttura} />
          )}
          {listaVociSpese.map((v) => (
            <CardImporto key={v.id} tema="costi" testId={`card-spesa-${v.id}`}
              titolo={v.descrizione} importo={v.importo}
              sub="Voce libera · modifica"
              onClick={() => apriVoce("spesa", v)} />
          ))}
        </SezionePannello>

        <SezionePannello tema="entrate" titolo="Entrate da avere" totale={bilancio.entrate_simulate}
          onAdd={() => apriVoce("entrata")} addLabel="Voce d'entrata" testId="totale-entrate">
          {bilancio.n_quota_standard > 0 && (
            <CardImporto tema="entrate" testId="card-quote-struttura"
              titolo="Quote partecipanti · struttura"
              importo={totaleQuoteStruttura}
              sub={`${bilancio.n_quota_standard} a quota base · dettaglio`}
              onClick={apriDettaglioQuote} />
          )}
          {bilancio.n_quota_standard > 0 && (
            <CardImporto tema="entrate" testId="card-maggiorazione"
              titolo="Maggiorazione quota" importo={bilancio.maggiorazione_quota}
              sub={`${bilancio.n_quota_standard} × ${eur(margineQuota)} a testa`}
              onClick={apriDettaglioMaggiorazione} />
          )}
          {bilancio.totale_override > 0 && (
            <CardImporto tema="entrate" testId="card-quote-fisse"
              titolo="Quote fisse (override)" importo={bilancio.totale_override}
              sub={`${attive.filter((r) => r.tipo_quota === "override").length} person${attive.filter((r) => r.tipo_quota === "override").length === 1 ? "a" : "e"} · dettaglio`}
              onClick={apriDettaglioQuoteFisse} />
          )}
          {listaVociEntrate.map((v) => (
            <CardImporto key={v.id} tema="entrate" testId={`card-entrata-${v.id}`}
              titolo={v.descrizione} importo={v.importo}
              sub="Voce libera · modifica"
              onClick={() => apriVoce("entrata", v)} />
          ))}
        </SezionePannello>
      </div>

      {/* FASCIA DI CHIUSURA: il GAP è il saldo, non una voce d'entrata come le altre */}
      <button type="button" onClick={apriDettaglioGap}
        className="w-full rounded-3xl bg-slate-900 p-4 mb-6 text-left hover:bg-slate-800 active:scale-[0.99] transition-all shadow-[0_4px_16px_rgba(15,23,42,0.18)]"
        data-testid="card-gap">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-300 uppercase tracking-[0.1em]">Quota parrocchia (GAP)</p>
            <p className="text-xs text-slate-400 font-semibold mt-0.5">
              Costi {eur(bilancio.spese_totali)} − Entrate {eur(bilancio.entrate_simulate)} · tocca per la formula
            </p>
          </div>
          <p className={`font-display text-2xl font-extrabold shrink-0 ${bilancio.gap > 0 ? "text-amber-400" : "text-emerald-400"}`}>
            {eur(bilancio.gap)}
          </p>
        </div>
      </button>

      {dati.partecipanti.length === 0 ? (
        <Empty icon={Tent} text="Nessun partecipante" sub="Aggiungi utenti esistenti o persone esterne" />
      ) : (
        <>
          {/* RISCOSSIONE — intestazione della lista: quanto è entrato davvero */}
          <p className="label-app">Riscossione</p>
          <div className="card-app p-4 mb-4" data-testid="riscossione-panel">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="bilancio-versato">
                <p className="font-display text-lg font-extrabold text-emerald-600">{eur(bilancio.totale_versato)}</p>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Versato</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="bilancio-residuo">
                <p className={`font-display text-lg font-extrabold ${bilancio.residuo_da_incassare > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                  {eur(bilancio.residuo_da_incassare)}
                </p>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Da incassare</p>
              </div>
            </div>
            {/* Avanzamento incassi: versato / totale dovuto */}
            {(() => {
              const dovuto = bilancio.totale_versato + bilancio.residuo_da_incassare;
              const perc = dovuto > 0 ? Math.min(100, Math.round((bilancio.totale_versato / dovuto) * 100)) : 0;
              return (
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${perc}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-400 font-semibold mt-1.5">
                    {perc}% incassato su {eur(dovuto)} di quote dovute
                  </p>
                </div>
              );
            })()}
          </div>

          {/* --- Filtro per annata. Il filtro è LATO SERVER: i partecipanti
               delle altre edizioni non arrivano nemmeno al browser. --- */}
          {(dati.stagioni?.length > 1 || dati.stagione !== dati.stagione_corrente) && (
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="camposcuola-stagioni">
              <CalendarRange className="h-4 w-4 text-slate-400 shrink-0" />
              {[...(dati.stagioni || [])].map((st) => (
                <button key={st} type="button" onClick={() => setStagione(st)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${
                    dati.stagione === st ? "bg-slate-900 text-white border-slate-900"
                                         : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"}`}
                  data-testid={`stagione-${st}`}>
                  {st}{st === dati.stagione_corrente ? " · in corso" : ""}
                </button>
              ))}
              <button type="button" onClick={() => setStagione("tutte")}
                className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${
                  dati.stagione === "tutte" ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"}`}
                data-testid="stagione-tutte">
                Tutte
              </button>
              {dati.stagione === "tutte" && (
                <span className="text-[11px] text-amber-700 font-semibold">
                  Vista di tutte le annate: i totali sommano edizioni diverse.
                </span>
              )}
            </div>
          )}

          {/* Selezione multipla per la rimozione in blocco */}
          <div className="flex gap-2 mb-3">
            <button type="button"
              className={bulkMode ? "btn-secondary !py-2 text-xs" : "btn-ghost !py-2 text-xs"}
              onClick={() => { setBulkMode(!bulkMode); setSelected([]); }}
              data-testid="camposcuola-bulk-toggle">
              {bulkMode ? "Annulla selezione" : "Seleziona"}
            </button>
          </div>

          {bulkMode && (
            <BulkBar count={selected.length} total={dati.partecipanti.length}
              onSelectAll={() => setSelected(
                selected.length === dati.partecipanti.length ? [] : dati.partecipanti.map((p) => p.id)
              )}
              onClear={() => { setBulkMode(false); setSelected([]); }}
              testId="camposcuola-bulk-bar"
              actions={
                <ConfirmAction onConfirm={bulkDelete}
                  title={`Rimuovere ${selected.length} partecipanti dal camposcuola?`}
                  description="I loro dati del camposcuola, comprese eventuali informazioni sanitarie, vengono eliminati."
                  testId="camposcuola-bulk-delete-confirm">
                  <button type="button" disabled={selected.length === 0}
                    className="btn-danger disabled:opacity-40 !py-2" data-testid="camposcuola-bulk-delete-btn">
                    Rimuovi {selected.length > 0 && `(${selected.length})`}
                  </button>
                </ConfirmAction>
              } />
          )}

          <div className="relative mb-3">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" className="input-app !pl-11" placeholder="Cerca partecipante per nome o contatto..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="camposcuola-search-input" />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {FILTRI.map(({ key, label, icon: Icon }) => {
              const count = key === "tutti"
                ? dati.partecipanti.length
                : dati.partecipanti.filter((p) => { const r = righeById.get(p.id); return r && statoDi(r) === key; }).length;
              const active = filtroStato === key;
              return (
                <button key={key} type="button" onClick={() => setFiltroStato(key)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "bg-slate-900 text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-400"}`}
                  data-testid={`camposcuola-filtro-${key}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
                </button>
              );
            })}
          </div>

          {(() => {
            const q = search.trim().toLowerCase();
            let lista = dati.partecipanti;
            if (filtroStato !== "tutti") lista = lista.filter((p) => { const r = righeById.get(p.id); return r && statoDi(r) === filtroStato; });
            // Il codice fiscale è un campo dismesso (non più raccolto né
            // restituito dall'API): la ricerca non lo usa più.
            if (q) lista = lista.filter((p) => `${p.nome || ""} ${p.cognome || ""} ${p.contatto || ""}`.toLowerCase().includes(q));
            if (lista.length === 0) return (
              <div className="card-app p-6 text-center"><p className="text-sm text-slate-400 font-semibold">Nessun partecipante corrisponde ai filtri correnti</p></div>
            );
            const byNome = (a, b) =>
              `${a.nome || ""} ${a.cognome || ""}`.toLowerCase().localeCompare(`${b.nome || ""} ${b.cognome || ""}`.toLowerCase(), "it");
            const GRUPPI = [
              { chiave: "responsabile", titolo: "Responsabili" },
              { chiave: "animatore", titolo: "Animatori" },
              { chiave: "ospite", titolo: "Ospiti" },
            ];
            const rigaPartecipante = (p) => {
              const riga = righeById.get(p.id);
              if (!riga) return null;
              const stato = statoDi(riga);
              const meta = [
                p.tipo === "esterno" ? "Esterno" : null,
                riga.tipo_quota === "override" ? `Quota fissa${p.motivo_override ? ` (${ETICHETTA_MOTIVO[p.motivo_override] || p.motivo_override})` : ""}` : null,
                riga.notti < nottiTot && riga.notti > 0 ? nottiLabel(riga.notti) : null,
              ].filter(Boolean).join(" · ");
              const isSel = selected.includes(p.id);
              return (
                <button key={p.id}
                  className={`w-full flex items-center gap-3 px-4 sm:px-5 py-4 text-left hover:bg-slate-50/60 transition-colors ${bulkMode && isSel ? "bg-amber-50" : ""}`}
                  onClick={() => bulkMode
                    ? setSelected((x) => x.includes(p.id) ? x.filter((y) => y !== p.id) : [...x, p.id])
                    : apri(p)}
                  data-testid={`partecipante-${p.id}`}>
                  {bulkMode && <BulkCheck checked={isSel} onClick={() => setSelected((x) => x.includes(p.id) ? x.filter((y) => y !== p.id) : [...x, p.id])} testId={`partecipante-check-${p.id}`} />}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 truncate">{p.nome} {p.cognome}</p>
                    {meta && <p className="text-xs text-slate-400 font-semibold truncate">{meta}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-700">{eur(p.importo_versato)} / {eur(riga.dovuto)}</p>
                    {riga.residuo > 0 && <p className="text-xs text-red-500 font-bold">mancano {eur(riga.residuo)}</p>}
                  </div>
                  <div className="shrink-0">{STATO_BADGE[stato]}</div>
                </button>
              );
            };
            return (
              <div className="space-y-5">
                {GRUPPI.map(({ chiave, titolo }) => {
                  const gruppo = lista.filter((p) => (p.ruolo || "animatore") === chiave).sort(byNome);
                  if (gruppo.length === 0) return null;
                  return (
                    <div key={chiave} data-testid={`gruppo-${chiave}`}>
                      <p className="label-app">{titolo} ({gruppo.length})</p>
                      <div className="card-app divide-y divide-slate-50">
                        {gruppo.map(rigaPartecipante)}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}

      {/* SHEET DETTAGLIO CARD ("come si arriva a questo numero") */}
      <BottomSheet open={!!dettaglio} onOpenChange={(o) => { if (!o) setDettaglio(null); }} title={dettaglio?.titolo || ""} testId="dettaglio-sheet">
        {dettaglio && (
          <div>
            {dettaglio.righe.length === 0 && <p className="text-sm text-slate-400 font-semibold py-2">Nessuna voce.</p>}
            {dettaglio.righe.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-50">
                <p className="text-sm font-semibold text-slate-600 min-w-0">{r.label}</p>
                <p className="text-sm font-bold text-slate-900 shrink-0 whitespace-nowrap">{r.value}</p>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 mb-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Totale</p>
              <p className="font-display text-lg font-extrabold text-slate-900">{eur(dettaglio.totale)}</p>
            </div>
            {dettaglio.note && <p className="text-xs text-slate-400 font-semibold">{dettaglio.note}</p>}
          </div>
        )}
      </BottomSheet>

      {/* SHEET VOCE LIBERA */}
      <BottomSheet open={!!voceEdit} onOpenChange={(o) => { if (!o) setVoceEdit(null); }}
        title={voceEdit?.id ? "Modifica voce" : voceEdit?.tipo === "spesa" ? "Nuova voce di costo" : "Nuova voce d'entrata"}
        testId="voce-sheet">
        {voceEdit && (
          <form onSubmit={salvaVoce}>
            <Field label="Descrizione">
              <input className="input-app" required value={voceEdit.descrizione}
                onChange={(e) => setVoceEdit({ ...voceEdit, descrizione: e.target.value })}
                placeholder={voceEdit.tipo === "spesa" ? "Es. Merende, materiale attività..." : "Es. Autofinanziamento torneo..."}
                data-testid="voce-descrizione-input" />
            </Field>
            <Field label="Importo (€)">
              <div className="relative">
                <Euro className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="number" step="0.01" min="0" required className="input-app !pl-10" value={voceEdit.importo}
                  onChange={(e) => setVoceEdit({ ...voceEdit, importo: e.target.value })} data-testid="voce-importo-input" />
              </div>
            </Field>
            <button className="btn-primary w-full" data-testid="voce-submit-btn">Salva voce</button>
            {voceEdit.id && (
              <div className="mt-3 flex justify-end">
                <DeleteButton onConfirm={eliminaVoce} entity="questa voce" testId="voce-delete-btn" />
              </div>
            )}
          </form>
        )}
      </BottomSheet>

      {/* SHEET UNICO DI AGGIUNTA: staff con account (multi-selezione) o ospite esterno */}
      <BottomSheet open={bulkOpen} onOpenChange={setBulkOpen} title="Aggiungi al camposcuola"
        description={addTab === "staff"
          ? "Seleziona animatori, aiuti e responsabili: ruolo e notti si impostano in automatico. Notti o quote particolari si ritoccano poi dalla lista."
          : "Persona senza account nell'app: sarà inserita come ospite col ruolo che scegli."} testId="bulk-staff-sheet">
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setAddTab("staff")} className={addTab === "staff" ? "btn-primary !py-2 flex-1" : "btn-ghost !py-2 flex-1"} data-testid="add-tab-staff">Staff con account</button>
          <button type="button" onClick={() => setAddTab("ospite")} className={addTab === "ospite" ? "btn-primary !py-2 flex-1" : "btn-ghost !py-2 flex-1"} data-testid="add-tab-ospite">Ospite esterno</button>
        </div>

        {addTab === "ospite" ? formPartecipante : (<>
        <div className="relative mb-3">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="search" className="input-app !pl-11" placeholder="Cerca per nome o nome utente..." value={bulkSearch}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            onChange={(e) => setBulkSearch(e.target.value)} data-testid="bulk-staff-search" />
        </div>

        {staffAggiungibile.length > 1 && (
          <button type="button" className="text-xs font-bold text-amber-600 mb-2"
            onClick={() => setBulkSel(bulkSel.length === bulkFiltrati.length ? [] : bulkFiltrati.map((u) => u.id))}
            data-testid="bulk-staff-tutti">
            {bulkSel.length === bulkFiltrati.length ? "Deseleziona tutti" : `Seleziona tutti (${bulkFiltrati.length})`}
          </button>
        )}

        <div className="max-h-72 overflow-y-auto space-y-1 border border-slate-100 rounded-2xl p-1.5 mb-4">
          {bulkFiltrati.length === 0 ? (
            <p className="text-xs text-slate-400 font-semibold text-center py-6">
              {staffAggiungibile.length === 0 ? "Tutto lo staff è già nel camposcuola" : "Nessuno trovato"}
            </p>
          ) : bulkFiltrati.map((u) => {
            const sel = bulkSel.includes(u.id);
            return (
              <button key={u.id} type="button"
                onClick={() => setBulkSel((s) => s.includes(u.id) ? s.filter((x) => x !== u.id) : [...s, u.id])}
                className={`w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${sel ? "bg-amber-50 text-amber-800" : "hover:bg-slate-50 text-slate-700"}`}
                data-testid={`bulk-staff-item-${u.id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <BulkCheck checked={sel} onClick={() => setBulkSel((s) => s.includes(u.id) ? s.filter((x) => x !== u.id) : [...s, u.id])} testId={`bulk-staff-check-${u.id}`} size={18} />
                  <span className="truncate">{u.nome} {u.cognome}</span>
                </span>
                <span className={`text-xs shrink-0 ${u.role === "responsabile" ? "text-emerald-600" : u.role === "animatore" ? "text-amber-500" : "text-sky-500"}`}>
                  {RUOLI_LABEL[u.role]}
                </span>
              </button>
            );
          })}
        </div>

        <button className="btn-primary w-full" disabled={bulkSaving || bulkSel.length === 0} onClick={salvaBulk} data-testid="bulk-staff-submit">
          {bulkSaving ? "Aggiunta..." : `Aggiungi ${bulkSel.length > 0 ? `(${bulkSel.length})` : ""}`}
        </button>
        </>)}
      </BottomSheet>

      {/* SHEET PARTECIPANTE (solo modifica: l'aggiunta passa dallo sheet unico) */}
      <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Modifica partecipante" testId="partecipante-sheet">
        {formPartecipante}
      </BottomSheet>

      {/* SHEET IMPOSTAZIONI EVENTO */}
      <BottomSheet open={impOpen} onOpenChange={chiudiImpostazioni} title="Impostazioni evento" description="Modificando i valori, quota e GAP si aggiornano in anteprima. Le voci di costo/entrata si gestiscono dalle card." testId="evento-sheet">
        {impForm && (
        <form onSubmit={salvaImpostazioni}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Costo notte struttura (€)">
              <input type="number" step="0.01" min="0" required className="input-app" value={impForm.costo_notte_struttura} onChange={(e) => patchImp("costo_notte_struttura", e.target.value)} data-testid="evento-costo-notte-input" />
            </Field>
            <Field label="Notti totali evento">
              <input type="number" min="1" required className="input-app" value={impForm.notti_totali_evento} onChange={(e) => patchImp("notti_totali_evento", e.target.value)} data-testid="evento-notti-input" />
            </Field>
          </div>
          <Field label="Quota base arrotondata (€, facoltativa)" hint={impForm.override_globale_quota_base === "" ? "Vuota: chi è a quota base paga la quota matematica esatta" : "Chi è a quota base paga questa cifra tonda; la differenza finisce nel GAP"}>
            <div className="relative">
              <Euro className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="number" step="0.01" min="0" className="input-app !pl-10" value={impForm.override_globale_quota_base}
                onChange={(e) => patchImp("override_globale_quota_base", e.target.value)} data-testid="evento-override-input"
                placeholder={bilancio.quota_matematica !== null ? bilancio.quota_matematica.toFixed(2) : "es. 190"} />
            </div>
          </Field>
          <div className="rounded-2xl bg-slate-50 p-3 mb-4 grid grid-cols-2 gap-2 text-center" data-testid="evento-preview">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Quota base</p>
              <p className="font-display font-extrabold text-slate-900">{bilancio.quota_applicata !== null ? eur(bilancio.quota_applicata) : "—"}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">GAP parrocchia</p>
              <p className={`font-display font-extrabold ${bilancio.gap > 0 ? "text-red-500" : "text-emerald-600"}`}>{eur(bilancio.gap)}</p>
            </div>
          </div>
          <button className="btn-primary w-full" data-testid="evento-submit-btn">Salva impostazioni</button>
        </form>
        )}
      </BottomSheet>
    </div>
  );
}
